import { CatalogNode } from "./catalogTree";
import { deriveGrantRef } from "./grantRef";

/**
 * A grantable access target — a pack or a (sub)catalog the admin can grant a user.
 * `ref` is the id the grants API expects; `path` is the human breadcrumb for the picker.
 */
export interface GrantTarget {
  kind: "pack" | "catalog";
  ref: string;
  title: string;
  /** Breadcrumb like "Apologetics › Foundations" for disambiguation in the picker. */
  path: string;
}

/**
 * Flatten a loaded catalog tree into the list of things an admin can grant: every
 * (sub)catalog and every pack, de-duplicated by kind+ref. Pure, so the admin grant
 * picker doesn't have to know how the tree was built. The synthetic root (empty id)
 * is skipped; its children are the real top-level catalogs.
 *
 * `baseUrl` is this deployment's `CONTENT_BASE_URL` (`worker/wrangler.toml`) — every
 * `ref` is derived from a node's own fetch URL (`CatalogPackEntry.manifestUrl` /
 * `CatalogNode.entryUrl`) via {@link deriveGrantRef}, **not** from the node's `id`: a
 * catalog document's declared `id` need not match its actual R2 path segment (see
 * `deriveGrantRef`'s own doc comment), so a bare-`id` ref could silently grant the
 * wrong (or an unreachable) manifest URL. A node whose ref isn't derivable (e.g. an
 * `entry.inline` sub-catalog, or a URL outside `baseUrl`) is skipped — it isn't
 * independently grantable, never offered with a guessed ref.
 */
export function grantTargets(root: CatalogNode | null | undefined, baseUrl: string): GrantTarget[] {
  if (!root) return [];
  const out: GrantTarget[] = [];
  const seen = new Set<string>();
  const add = (kind: "pack" | "catalog", ref: string | null, title: string, path: string) => {
    if (!ref) return;
    const key = `${kind}:${ref}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ kind, ref, title, path });
  };
  const walk = (node: CatalogNode, trail: string[]) => {
    for (const p of node.packs) {
      add("pack", deriveGrantRef(baseUrl, "pack", p.manifestUrl), p.title, [...trail, p.title].join(" › "));
    }
    for (const c of node.children) {
      add("catalog", deriveGrantRef(baseUrl, "catalog", c.entryUrl), c.title, [...trail, c.title].join(" › "));
      walk(c, [...trail, c.title]);
    }
  };
  walk(root, root.title ? [root.title] : []);
  return out;
}

/**
 * Unwrap a single wrapping catalog (the master root-index) so callers see the REAL top-level
 * root catalogs instead of the index wrapper. The admin fetches ONE catalog (the master
 * root-index `all-root-catalogs.json`), so `buildCatalogTree` wraps it: synthetic-root →
 * [master-index] → [the real roots]. With multiple direct children (several fetched catalogs)
 * those ARE the roots already — no unwrap. Shared by {@link rootCatalogTargets} and
 * {@link rootCatalogNodes} so they can't drift on what counts as "top level."
 */
function unwrapRootLevel(root: CatalogNode): CatalogNode[] {
  let level: CatalogNode[] = root.children;
  if (level.length === 1 && level[0].children.length > 0) {
    level = level[0].children;
  }
  return level;
}

/**
 * The tree's TOP-LEVEL root catalogs — the direct children of the synthetic root, i.e.
 * the master-index entries ("Adan's Full Private Catalog", "Be Equipped University", …).
 * Unlike {@link grantTargets} this does NOT recurse or de-dup across the whole tree, so
 * a root that ALSO appears nested inside another root (e.g. Be Equipped University lives
 * both as its own root AND inside the full-private umbrella) is STILL listed here at the
 * top level — otherwise grantTargets' whole-tree dedup claims it at its nested depth and
 * the picker's default view drops it. This is the invite picker's default list. `ref` is
 * derived from each child's own `entryUrl`; a child whose ref isn't derivable is skipped.
 */
export function rootCatalogTargets(root: CatalogNode | null | undefined, baseUrl: string): GrantTarget[] {
  if (!root) return [];
  const out: GrantTarget[] = [];
  const seen = new Set<string>();
  for (const c of unwrapRootLevel(root)) {
    const ref = deriveGrantRef(baseUrl, "catalog", c.entryUrl);
    if (!ref || seen.has(ref)) continue;
    seen.add(ref);
    out.push({ kind: "catalog", ref, title: c.title, path: c.title });
  }
  return out;
}

/**
 * A (sub)catalog paired with its `GrantTarget` AND the underlying `CatalogNode` — the node
 * reference is what lets a caller lazily drill into `node.children`/`node.packs` one level
 * at a time without re-walking (or re-fetching) the whole tree.
 */
export interface CatalogTargetNode {
  node: CatalogNode;
  target: GrantTarget;
}

/**
 * Same top-level list as {@link rootCatalogTargets}, but keeping each root's underlying
 * `CatalogNode` alongside its `GrantTarget` so a browse UI (docs/365-ADMIN-USER-COMPLETION-
 * AND-CATALOGS-TABS.md item 2) can expand a root to reveal its own children/packs in memory
 * — no second fetch, the whole tree is already loaded. `rootCatalogTargets` itself is
 * unchanged (still just the flat `GrantTarget[]` the invite picker consumes) — this is a
 * strict superset via the SAME {@link unwrapRootLevel} so the two can never disagree on what
 * counts as a root.
 */
export function rootCatalogNodes(root: CatalogNode | null | undefined, baseUrl: string): CatalogTargetNode[] {
  if (!root) return [];
  const out: CatalogTargetNode[] = [];
  const seen = new Set<string>();
  for (const c of unwrapRootLevel(root)) {
    const ref = deriveGrantRef(baseUrl, "catalog", c.entryUrl);
    if (!ref || seen.has(ref)) continue;
    seen.add(ref);
    out.push({ node: c, target: { kind: "catalog", ref, title: c.title, path: c.title } });
  }
  return out;
}

/**
 * One level of a catalog node's own contents — its direct child (sub)catalogs (node-paired,
 * for further drill-down) and its direct packs (leaf `GrantTarget`s — a pack has no
 * children). A child whose ref isn't derivable is skipped, exactly like {@link grantTargets}/
 * {@link rootCatalogTargets}. `path`/`title` are relative to `node` (just the child's own
 * title), not a full breadcrumb from the tree root — the caller already knows its own
 * ancestry (it's the one that expanded this node).
 */
export interface CatalogChildTargets {
  catalogs: CatalogTargetNode[];
  packs: GrantTarget[];
}

export function catalogChildTargets(node: CatalogNode | null | undefined, baseUrl: string): CatalogChildTargets {
  if (!node) return { catalogs: [], packs: [] };
  const catalogs: CatalogTargetNode[] = [];
  const seenCatalogs = new Set<string>();
  for (const c of node.children) {
    const ref = deriveGrantRef(baseUrl, "catalog", c.entryUrl);
    if (!ref || seenCatalogs.has(ref)) continue;
    seenCatalogs.add(ref);
    catalogs.push({ node: c, target: { kind: "catalog", ref, title: c.title, path: c.title } });
  }
  const packs: GrantTarget[] = [];
  const seenPacks = new Set<string>();
  for (const p of node.packs) {
    const ref = deriveGrantRef(baseUrl, "pack", p.manifestUrl);
    if (!ref || seenPacks.has(ref)) continue;
    seenPacks.add(ref);
    packs.push({ kind: "pack", ref, title: p.title, path: p.title });
  }
  return { catalogs, packs };
}

/**
 * Depth of a `GrantTarget.path` breadcrumb — the number of `"›"` separators. A
 * top-level catalog's `path` is just its own title (depth 0); each nested level of
 * `"Parent › Child"` adds one. Still exported for tests/back-compat; the invite picker
 * now uses {@link rootCatalogTargets} for its default view.
 */
export function pathDepth(path: string): number {
  let depth = 0;
  for (const ch of path) if (ch === "›") depth += 1;
  return depth;
}
